/**
 * Every computed line in v2, and what it is computed from.
 *
 * Transcribed from the engine modules under services/valuator/app/engine/v2/,
 * which are the only authority: the spec describes intent, the engine decides
 * the number. Where the two disagreed the engine won, and the note says so.
 *
 * This is deliberately NOT v1's list. Several of v1's formulas are wrong for
 * v2 and would mislead precisely the reader who came here to check something:
 *
 *   - v1 computes cost of goods sold as revenue x a COGS ratio. v2 takes cost
 *     of sales from its own tab, as a NEGATIVE figure that already includes
 *     direct wages, so gross profit is revenue PLUS cost of sales.
 *   - v1 treats cash as a balancing plug. v2 derives cash from the cashflow
 *     and reports the balance check honestly on its own line.
 *   - v2 has COGS, Indirect Costs and Fixed Assets as tabs in their own
 *     right; v1 entered those lines on the P&L.
 *
 * `sources` names the tabs a line reads from, which is the cross-tab part: a
 * figure on the P&L is very often decided somewhere else entirely, and that
 * is the thing a reader cannot see from the sheet alone.
 */

// The shapes and the tab labels moved up to `views/v2/formulaTypes.ts` when the
// formulas panel learned about both disciplines: a panel in `accounting/` importing
// the valuation list would cross the boundary, and `boundary.test.ts` caught it.
// Re-exported so every existing importer of this module still works.
export type { FormulaEntry, FormulaSection, TabKey } from '../formulaTypes';
export { TAB_LABELS } from '../formulaTypes';

import type { FormulaSection, TabKey } from '../formulaTypes';

export const TAB_ORDER: TabKey[] = [
  'pnl',
  'cashflow',
  'balance-sheet',
  'equity',
  'cogs',
  'indirect',
  'fixed-assets',
  'staffing',
  'control',
  'debt',
  'leases',
  'provisions',
  'pensions',
  'impairment',
  'deferred-tax',
];

export const FORMULAS: Record<TabKey, FormulaSection[]> = {
  cogs: [
    {
      section: 'Stock',
      entries: [
        {
          label: 'Closing Stock',
          formula: 'Opening Stock + Goods Purchased + Goods Sold',
          note: 'Goods Sold is negative, so this is the template’s SUM(r8:r10).',
        },
        {
          label: 'Goods Purchased',
          formula: 'Entered, or − Goods Sold when not supplied',
          note: 'The fallback means no stock movement. Supplying the line enables real stock modelling.',
        },
        {
          label: 'Goods Sold (forecast years)',
          formula:
            '− Total Revenue × (goods sold ÷ revenue in the last historical year)',
          sources: ['pnl'],
          note: 'Only fills periods left blank; anything entered takes precedence. Walks back past historical years with no revenue.',
        },
      ],
    },
    {
      section: 'Cost of sales',
      entries: [
        {
          label: 'Direct Wages',
          formula: 'Direct Staff headcount × cost per head',
          sources: ['staffing'],
        },
        {
          label: 'Cost of Sales',
          formula: 'Goods Sold − Direct Wages',
          sources: ['staffing'],
          note: 'NEGATIVE. Goods Sold is already negative and Direct Wages is a positive magnitude, so subtracting makes it more negative. Direct staff is excluded from Total Indirect Staff Cost, so it is not counted twice.',
        },
      ],
    },
  ],

  staffing: [
    {
      section: 'Departments',
      entries: [
        {
          label: 'Direct Staff Cost',
          formula: 'headcount × cost per head',
          note: 'Feeds COGS as Direct Wages, not the P&L staff line.',
        },
        {
          label: 'Total Indirect Staff Cost',
          formula: 'Σ over the four indirect departments of (headcount × cost per head)',
          note: 'Excludes Direct Staff deliberately — that sits in cost of sales.',
        },
        {
          label: 'Total Staff Expense',
          formula: 'Total Indirect Staff Cost + Travel & Entertaining + Other Staff Costs',
          note: 'This is the figure the P&L shows as Staff Costs.',
        },
      ],
    },
  ],

  indirect: [
    {
      section: 'Groups',
      entries: [
        {
          label: 'Total Advertising & Marketing',
          formula: 'Advertising + Marketing',
        },
        {
          label: 'Depreciation & Amortisation (forecast years only)',
          formula: 'Depreciation charge + Amortisation charge',
          sources: ['fixed-assets'],
          note: 'Added to administration in FORECAST periods only. Historical administration already contains D&A as filed, so both are gross of it and the P&L strips it once.',
        },
        {
          label: 'Total Administration',
          formula:
            'Insurance + Rent + Utilities + Salaries + Other Admin + D&A (forecast years)',
          sources: ['fixed-assets'],
        },
        {
          label: 'Total Finance Costs',
          formula: 'Bad Debt + Bank Charges + Legal & Professional Fees + Other Finance Costs',
        },
        {
          label: 'Total Indirect Costs',
          formula: 'Distribution + Advertising & Marketing + Administration + Finance Costs',
          note: 'All four groups. The template’s r31 sums only two and is not a grand total.',
        },
      ],
    },
  ],

  'fixed-assets': [
    {
      section: 'Each asset class, rolled forward',
      entries: [
        {
          label: 'Cost c/fwd',
          formula: 'Cost b/fwd + Additions + Disposals (cost)',
          note: 'Disposals are entered negative.',
        },
        {
          label: 'Charge',
          formula:
            'Entered charge, or Cost c/fwd × (months per period ÷ depreciation period) in forecast periods',
          note: 'The default applies only where no charge is entered, and is capped so the asset cannot depreciate below nil.',
        },
        {
          label: 'Accumulated c/fwd',
          formula: 'Accumulated b/fwd + Charge + Disposals (charge)',
        },
        {
          label: 'Net book value',
          formula: 'Cost c/fwd − Accumulated c/fwd',
        },
      ],
    },
    {
      section: 'Totals consumed elsewhere',
      entries: [
        {
          label: 'Gross Fixed Assets',
          formula: 'Intangible Cost c/fwd + Tangible Cost c/fwd',
        },
        {
          label: 'Accumulated Depreciation',
          formula: '− (Intangible + Tangible Accumulated c/fwd)',
          note: 'Held negative, because the balance sheet adds it to gross.',
        },
        {
          label: 'Net Fixed Assets',
          formula: 'Gross Fixed Assets + Accumulated Depreciation',
        },
        {
          label: 'Depreciation charge / Amortisation charge',
          formula: 'Tangible charge / Intangible charge',
          note: 'Depreciation is the TANGIBLE charge; amortisation is the intangible one.',
        },
        {
          label: 'Total Additions',
          formula: 'Intangible Additions + Tangible Additions',
          note: 'The cashflow reads this as capex.',
        },
      ],
    },
  ],

  pnl: [
    {
      section: 'Revenue and gross profit',
      entries: [
        { label: 'Total Revenue', formula: 'Sales 1 + Sales 2 + Sales 3' },
        {
          label: 'Cost of Sales',
          formula: 'Taken from the COGS tab',
          sources: ['cogs'],
          note: 'Negative, and already includes direct wages.',
        },
        {
          label: 'Gross Profit',
          formula: 'Total Revenue + Cost of Sales',
          sources: ['cogs'],
          note: 'PLUS, not minus, because cost of sales is negative.',
        },
      ],
    },
    {
      section: 'Indirect costs',
      entries: [
        {
          label: 'Staff Costs',
          formula: 'Total Staff Expense',
          sources: ['staffing'],
        },
        {
          label: 'Administration',
          formula:
            '(Distribution + Total Administration) − Depreciation − Amortisation',
          sources: ['indirect', 'fixed-assets'],
          note: 'D&A is stripped here in EVERY period and charged once below, so it is never counted twice. This is why operating profit is struck before D&A.',
        },
        {
          label: 'Total Indirect Costs',
          formula: 'Staff Costs + Advertising & Marketing + Administration + Finance Costs',
          sources: ['staffing', 'indirect'],
        },
      ],
    },
    {
      section: 'Profit',
      entries: [
        {
          label: 'Operating Profit',
          formula: 'Gross Profit − Total Indirect Costs',
          note: 'Struck BEFORE depreciation and amortisation — it is an EBITDA figure despite the name.',
        },
        {
          label: 'EBITDA',
          formula: 'Operating Profit + Interest receivable & other income',
        },
        {
          label: 'Profit before Taxation',
          formula: 'EBITDA − Interest payable − Depreciation − Amortisation',
          sources: ['fixed-assets'],
        },
        {
          label: 'Taxation',
          formula: 'Entered, or Profit before Taxation × tax rate when that profit is positive',
          note: 'A supplied taxation row always wins. No charge is raised on a loss.',
        },
        { label: 'Profit after Taxation', formula: 'Profit before Taxation − Taxation' },
        {
          label: 'Profits after Dividends',
          formula: 'Profit after Taxation − Dividends',
          sources: ['control'],
        },
        {
          label: 'Retained Profits',
          formula: 'Profits after Dividends + FRS102 adjustment',
          note: 'This is what accumulates into retained earnings on the balance sheet.',
        },
      ],
    },
  ],

  control: [
    {
      section: 'Every section',
      entries: [
        {
          label: 'Closing balance',
          formula: 'Opening + P&L movement + cash movement',
        },
        {
          label: 'Opening balance',
          formula: 'The previous period’s closing balance',
          note: 'The first period opens from the opening balance sheet, not from zero.',
        },
      ],
    },
    {
      section: 'Trade debtors and creditors',
      entries: [
        {
          label: 'Trade debtor / creditor closing balance',
          formula:
            'Actual when supplied, otherwise (invoiced + VAT) × (debtor or creditor days ÷ days in period)',
          sources: ['pnl'],
          note: 'v2 invoices TOTAL revenue. The template invoiced Sales 1 only, which understates debtors and output VAT the moment the other revenue lines are used.',
        },
        {
          label: 'Cash movement',
          formula: 'Closing − Opening − Invoiced − VAT',
          note: 'Derived as the balancing figure, matching the template.',
        },
      ],
    },
    {
      section: 'VAT',
      entries: [
        {
          label: 'Closing VAT',
          formula: 'Actual when supplied, otherwise the net VAT of the quarter just ended',
          note: 'Settled quarterly in arrears.',
        },
      ],
    },
  ],

  cashflow: [
    {
      section: 'Operating',
      entries: [
        {
          label: 'Net income',
          formula: 'Profit after Taxation + FRS102 adjustment',
          sources: ['pnl'],
          note: 'FRS102 is an equity movement, so it belongs here for retained earnings and cash to agree.',
        },
        {
          label: 'Add back depreciation & amortisation',
          formula: 'Depreciation + Amortisation',
          sources: ['fixed-assets'],
        },
        {
          label: 'Change in stocks',
          formula: '− (Closing Stock − previous Closing Stock)',
          sources: ['cogs'],
          note: 'Taken from the COGS tab; stock sits outside the control account. Missing from the template entirely.',
        },
        {
          label: 'Change in trade / other debtors',
          formula: '− movement in the control account balance',
          sources: ['control'],
          note: 'An asset increasing consumes cash.',
        },
        {
          label: 'Change in trade / other creditors',
          formula: '− movement in the control account balance',
          sources: ['control'],
          note: 'Creditor balances are held NEGATIVE in the control account, so a balance growing more negative is a liability increasing, which releases cash.',
        },
        {
          label: 'Corporation tax paid',
          formula: '− movement in the corporation tax creditor',
          sources: ['control'],
          note: 'An accrual adjustment, NOT the tax charge — net income above is already after tax.',
        },
        {
          label: 'Net operating cashflow',
          formula:
            'The lines above − profit on disposal − share of associates + provisions charged, unwound and paid + pension charge and contributions + credit-loss charge + deferred tax charge + change in other working capital lines',
          sources: ['provisions', 'pensions', 'impairment', 'deferred-tax'],
          note: 'Every non-cash charge in profit is added back here, and every gain taken out: the template rows alone are not the whole of operating cash. The export lists each on its own row below the statement.',
        },
      ],
    },
    {
      section: 'Investing and financing',
      entries: [
        {
          label: 'Fixed asset purchases',
          formula: '− Total Additions',
          sources: ['fixed-assets'],
          note: 'Right-of-use additions funded by a lease are taken back out under financing: no cash bought them.',
        },
        {
          label: 'Net investing cashflow',
          formula: 'Fixed asset purchases + disposal proceeds + movement in other investing lines',
          sources: ['fixed-assets'],
        },
        {
          label: 'Net financing cashflow',
          formula:
            'Equity injection + other equity cash + Loan drawdowns − Loan repayments − Dividends + movement in other borrowing lines − lease principal repaid',
          sources: ['control', 'debt', 'leases'],
          note: 'The rise in a lease liability from a new or remeasured lease is taken out: no cash moved.',
        },
      ],
    },
    {
      section: 'Cash',
      entries: [
        {
          label: 'Net cashflow',
          formula: 'Operating + Investing + Financing + effect of exchange rates on cash',
        },
        {
          label: 'Closing cash',
          formula: 'Opening cash + Net cashflow',
          note: 'The first period opens from the opening balance sheet. Cash is DERIVED here — it is not a balancing plug as it was in v1.',
        },
      ],
    },
  ],

  equity: [
    {
      section: 'Each component, rolled forward',
      entries: [
        {
          label: 'Opening',
          formula: 'The previous period’s closing balance',
          note:
            'Which is what makes this a reconciliation rather than a list: every ' +
            'period opens where the last one closed, and the first opens at the ' +
            'figure on the Opening balances.',
        },
        {
          label: 'Attributed movement',
          formula:
            'Profit + Other comprehensive income + Shares issued − Dividends + Share-based payments',
          sources: ['pnl', 'control'],
          note:
            'Dividends are held POSITIVE and subtracted. Adding them instead is a ' +
            'fault that shows up as exactly twice the dividend, which is why the ' +
            'sign is stated here rather than left to be inferred.',
        },
        {
          label: 'Other (unexplained)',
          formula: 'Closing − Opening − Attributed movement',
          note:
            'A residual, not a plug. The statement is built from the balance ' +
            'sheet’s own closing balances rather than recomputed from the inputs, ' +
            'so anything this line carries is movement nothing in the model ' +
            'accounts for — and showing it is the point. A reconciliation that ' +
            'always closed would be one that could never disagree.',
        },
        {
          label: 'Closing',
          formula: 'The balance sheet’s own figure for that component',
          sources: ['balance-sheet'],
          note:
            'Taken from the balance sheet rather than recomputed. Recomputing ' +
            'would give a statement that reconciles beautifully to figures nobody ' +
            'is looking at; the test that matters is that the total closes on the ' +
            'same total equity the balance sheet reports.',
        },
      ],
    },
    {
      section: 'Which components appear',
      entries: [
        {
          label: 'Share capital, reserves, retained earnings',
          formula: 'Always',
        },
        {
          label: 'Other comprehensive income reserve',
          formula: 'Only where there is any',
          note:
            'Presented separately because OCI never passes through profit — that ' +
            'is what makes it OTHER comprehensive income — so a reader can see ' +
            'which part of equity can still reach earnings.',
        },
        {
          label: 'A declared equity line',
          formula: 'Its closing balances, with the movement as a residual',
          note:
            'Share premium, non-controlling interests. Nothing states what caused ' +
            'the movement, so it is not attributed: a placing raises share capital ' +
            'and premium in whatever proportion the shares were priced at, and the ' +
            'filing reports only the result.',
        },
        {
          label: 'Shares issued',
          formula:
            'Equity investment from the Control Account — UNLESS the filing declares its own equity lines',
          sources: ['control'],
          note:
            'Where a filing states its equity components, the money raised is ' +
            'already inside the share premium it reports. Counting it again would ' +
            'show the amount raised twice, once as shares issued and once as ' +
            'unexplained movement cancelling it out.',
        },
      ],
    },
  ],

  debt: [
    {
      section: 'Rolled forward per period',
      entries: [
        { label: 'Opening', formula: 'The previous period’s closing balance' },
        {
          label: 'Interest',
          formula: 'Opening balance × the debt interest rate',
          note:
            'On the OPENING balance, so a drawdown part-way through a period does ' +
            'not earn a full period of interest.',
        },
        {
          label: 'Repaid',
          formula: 'min(the repayment entered, Opening + Drawdowns)',
          note:
            'Never more than is owed, drawdowns included. A repayment larger than ' +
            'the loan settles it and no more — a negative balance would read as ' +
            'money the lender owes the company.',
        },
        {
          label: 'Closing',
          formula: 'Opening + Drawdowns − Repaid',
        },
        {
          label: 'Split into current and non-current',
          formula: 'The proportion given on the opening balances, held constant',
          sources: ['balance-sheet'],
        },
      ],
    },
  ],

  leases: [
    {
      section: 'The liability, rolled forward',
      entries: [
        { label: 'Opening', formula: 'The previous period’s closing liability' },
        { label: 'Interest', formula: 'Opening liability × the lease discount rate' },
        {
          label: 'Payment',
          formula:
            'The payment entered; otherwise Opening × (months in the period ÷ remaining term) + Interest',
          note:
            'A zero counts as "not entered", as it does everywhere in this engine: ' +
            'forecast rows arrive zero-filled, and a lease nobody is paying is not ' +
            'a lease. Capped so a payment cannot take the liability below nothing.',
        },
        {
          label: 'Principal',
          formula: 'Payment − Interest',
          note: 'The part that reduces the obligation; the rest is a finance cost.',
        },
        {
          label: 'Closing',
          formula: 'Opening + Interest + Additions − Payment',
        },
      ],
    },
  ],

  provisions: [
    {
      section: 'Per provision, from its note',
      entries: [
        {
          label: 'Closing',
          formula:
            'Opening + Additions − Amounts used − Unused amounts reversed + Discount unwind',
          note:
            'Only for a provision whose note gives the movement. One without a ' +
            'note carries its closing balance forward unchanged, because a ' +
            'movement nobody stated is not a movement this engine will invent.',
        },
        {
          label: 'Charged to profit',
          formula: 'Additions − Unused amounts reversed',
          sources: ['pnl'],
          note:
            'A new obligation is an expense and an over-provision released is a ' +
            'credit. Amounts USED are not a charge — they were charged when the ' +
            'provision was made.',
        },
        {
          label: 'Discount unwind',
          formula: 'A finance cost, not an operating one',
          sources: ['pnl'],
        },
      ],
    },
  ],

  pensions: [
    {
      section: 'The net liability, rolled forward',
      entries: [
        {
          label: 'Closing',
          formula:
            'Opening + Current service cost + Past service cost + Net interest − Contributions paid + Remeasurement',
          note:
            'The NET liability — the obligation less the plan assets — because that is ' +
            'what a filing states. Only runs for a line with an IAS 19 note; without ' +
            'one the balance carries forward unchanged.',
        },
      ],
    },
    {
      section: 'Where each part goes, which is the whole point',
      entries: [
        {
          label: 'Current and past service cost',
          formula: 'An operating expense, with staff costs',
          sources: ['pnl'],
          note:
            'An employment cost, not an overhead. Past service cost joins it because a ' +
            'scheme amendment is recognised at once (IAS 19 §103).',
        },
        {
          label: 'Net interest',
          formula: 'A FINANCE cost',
          sources: ['pnl'],
          note:
            'IAS 19 §125. An operating figure that carried it would understate ' +
            'operating profit by the whole amount, which for a mature scheme can exceed ' +
            'the service cost.',
        },
        {
          label: 'Remeasurement',
          formula: 'Other comprehensive income, never reclassified',
          sources: ['pnl'],
          note:
            'IAS 19 §122. Positive is a LOSS. Run through profit it would show a loss ' +
            'nobody incurred in trading — and since it can never be recycled back, the ' +
            'split of equity would be wrong permanently, not just for a year.',
        },
        {
          label: 'Contributions paid',
          formula: 'The cash, and the only part of this that is cash',
          sources: ['cashflow'],
          note:
            'The service cost and the net interest are added back in the cash flow with ' +
            'the other non-cash charges. The remeasurement appears nowhere in it — it ' +
            'never passed through profit.',
        },
      ],
    },
  ],

  impairment: [
    {
      section: 'The allowance',
      entries: [
        {
          label: 'Movement',
          formula: '−(Charge − Recoveries)',
          note:
            'NEGATIVE, because the allowance is a contra-asset: it reduces the ' +
            'receivable beside it, which is how a filing presents it. The WRITE-OFF is ' +
            'deliberately absent — see below.',
        },
        {
          label: 'Charged to profit',
          formula: 'Charge − Recoveries',
          sources: ['pnl'],
          note:
            'An operating cost. The write-off is not a cost: it was charged when the ' +
            'allowance was raised, and charging it again would take the same loss twice.',
        },
        {
          label: 'Cash effect',
          formula: 'None',
          sources: ['cashflow'],
          note:
            'The charge is added back. The allowance already reduced the debtor balance, ' +
            'so the movement in trade debtors is net of it — adding the charge back is ' +
            'what stops the same figure being counted twice.',
        },
        {
          label: 'Why a write-off moves nothing',
          formula: 'Dr allowance, Cr trade receivables — both assets, so net nil',
          note:
            'Its other leg is inside trade debtors, which this engine derives rather ' +
            'than holding gross and net separately. Moving the allowance alone left ' +
            'total assets overstated by the write-off and the sheet out by the same. So ' +
            'the line tracks the CHARGE and will read higher than the filing’s own ' +
            'allowance note after a year with write-offs; where the filing states that ' +
            'balance it wins, and the balance check reports the difference.',
        },
      ],
    },
  ],

  'deferred-tax': [
    {
      section: 'The tax written-down value',
      entries: [
        {
          label: 'Allowance claimed',
          formula:
            '(Opening pool + Additions + Disposals) × the capital allowance rate × (months in the period ÷ 12)',
          sources: ['fixed-assets'],
          note:
            'Claimed on the pool INCLUDING the period’s additions: a company ' +
            'buying a machine claims on it in the year it buys it, which is most ' +
            'of why the tax pool runs ahead of the accounts. Disposals are held ' +
            'negative, so adding them reduces the pool.',
        },
        {
          label: 'Closing pool',
          formula: 'Opening + Additions + Disposals − Allowance claimed',
        },
      ],
    },
    {
      section: 'The balance and the charge',
      entries: [
        {
          label: 'Timing difference',
          formula: 'Net book value − Closing tax pool',
          sources: ['fixed-assets'],
          note:
            'Positive where the accounts carry more than the tax computation does, ' +
            'which is a taxable temporary difference and therefore a LIABILITY.',
        },
        {
          label: 'Deferred tax balance',
          formula: 'Timing difference × the tax rate for THAT period',
          note:
            'IAS 12.47: at the rate expected to apply when the difference ' +
            'reverses, not today’s rate. The UK has had three main rates in four ' +
            'years, so measuring a 2026 reversal at a 2023 rate is wrong by the ' +
            'difference between them.',
        },
        {
          label: 'Charge to profit',
          formula: 'The movement in the balance',
          sources: ['pnl'],
          note:
            'Measured from the balance the company already carried, not from zero. ' +
            'A company with accelerated allowances behind it would otherwise take ' +
            'the whole of that liability as a charge in the first period — a ' +
            'one-off tax bill it does not have.',
        },
        {
          label: 'When any of this applies',
          formula: 'Only when deferred tax derivation is switched on in Settings',
          note:
            'Off by default. An imported filing states its own deferred tax, and a ' +
            'stated figure always beats a derived one.',
        },
      ],
    },
  ],

  'balance-sheet': [
    {
      section: 'Assets',
      entries: [
        {
          label: 'Net Fixed Assets',
          formula: 'Gross Fixed Assets + Accumulated Depreciation',
          sources: ['fixed-assets'],
          note: 'Accumulated depreciation is negative, hence the addition.',
        },
        {
          label: 'Cash',
          formula: 'Closing cash on the Cashflow tab',
          sources: ['cashflow'],
          note: 'Derived from the cash flow, never a plug. In the export it is a live link to the Cashflow sheet.',
        },
        {
          label: 'Total Current Assets',
          formula: 'Stocks + Trade Debtors + Other Debtors + VAT Receivable + Cash + every declared current asset',
          sources: ['cogs', 'control', 'cashflow'],
        },
        {
          label: 'Investments',
          formula: 'Financial assets + equity-accounted investments (declared lines)',
          note: 'Shown on this row once, and taken out of the declared non-current assets below.',
        },
        {
          label: 'Total Assets',
          formula:
            'Total Current Assets + Net Fixed Assets + Investments + Deferred Tax Asset + every other declared non-current asset',
          note: 'A memo line (a credit-loss allowance, whose receivables are read net) is shown but in no total.',
        },
      ],
    },
    {
      section: 'Liabilities',
      entries: [
        {
          label: 'Trade Creditors, Corporation Tax, Other Creditors',
          formula: '− the control account closing balance',
          sources: ['control'],
          note: 'Held negative in the control account and presented positive here.',
        },
        {
          label: 'Total Current Liabilities',
          formula: 'Trade Creditors + Corporation Tax + Other Creditors + VAT owed + every declared current liability',
          sources: ['control'],
        },
        {
          label: 'Creditors over one year',
          formula: '− the control account closing balance + the derived deferred tax liability',
          sources: ['control', 'deferred-tax'],
        },
        {
          label: 'Total Liabilities',
          formula: 'Total Current Liabilities + Creditors over one year + every declared non-current liability',
          sources: ['control', 'provisions', 'leases', 'pensions'],
        },
        {
          label: 'Total Assets less Liabilities',
          formula: 'Total Assets − Total Liabilities',
        },
      ],
    },
    {
      section: 'Equity and the check',
      entries: [
        {
          label: 'Retained Earnings',
          formula:
            'Opening retained earnings + the running total of Retained Profits + movements taken straight to equity + transfers from reserves + equity cash movements',
          sources: ['pnl', 'equity'],
        },
        {
          label: 'Share Capital',
          formula: 'Opening share capital + equity movements in the control account',
          sources: ['control'],
        },
        {
          label: 'Other comprehensive income reserve',
          formula: 'The running total of other comprehensive income not taken to a reported reserve',
          sources: ['pnl', 'equity'],
        },
        {
          label: 'Total Equity',
          formula:
            'Share Capital + Share-based Payment Reserve + Retained Earnings + OCI reserve + every declared equity line',
          sources: ['equity'],
          note: 'A filed share capital line is the Share Capital row, so it is counted once, not again among the declared lines.',
        },
        {
          label: 'Balance check',
          formula: 'Total Assets less Liabilities − Total Equity',
          note: 'Must be nil. It is reported rather than absorbed — nothing is plugged to make it vanish.',
        },
        {
          label: 'Cash variance',
          formula: 'Implied cash − actual cash',
          sources: ['cashflow'],
          note: 'What cash would have to be for the sheet to balance, less what the cashflow produced. A diagnosis, not an adjustment.',
        },
      ],
    },
  ],

};
